import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import Navbar from '../components/Navbar';
import SEO from '../components/common/SEO';

const Section = ({ title, children }) => (
  <section className="mb-8" data-testid={`deletion-section-${title.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
    <h2 className="font-heading text-2xl text-white mb-3">{title}</h2>
    <div className="text-white/75 leading-relaxed space-y-3">{children}</div>
  </section>
);

const Bullets = ({ items }) => (
  <ul className="list-disc pl-6 space-y-2 marker:text-[#FF007F]">
    {items.map((item, i) => (
      <li key={i}>{item}</li>
    ))}
  </ul>
);

const DataDeletion = () => {
  return (
    <div className="min-h-screen bg-[#0a0a0a]">
      <SEO
        title="Data Deletion Instructions — Universal Performing Arts (UPA)"
        description="How to request deletion of your personal information held by Universal Performing Arts (UPA), including data received from Facebook and Instagram lead forms."
        canonical="https://www.upaschool.com/data-deletion"
      />
      <Navbar />

      <main className="max-w-3xl mx-auto px-6 lg:px-0 py-12 lg:py-20" data-testid="data-deletion-page">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/60 hover:text-[#FF007F] transition-colors text-sm mb-8"
          data-testid="deletion-back-link"
        >
          <ArrowLeft className="w-4 h-4" /> Back to Home
        </Link>

        <header className="mb-10">
          <p className="text-[#FF007F] text-sm uppercase tracking-widest mb-3">Legal</p>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-white mb-4">
            Data Deletion Instructions
          </h1>
          <p className="text-white/60 text-base">
            How to ask Universal Performing Arts (UPA) to delete your personal information.
            Last updated: June 2026.
          </p>
        </header>

        <Section title="Your Right to Deletion">
          <p>
            Under UK GDPR you can ask us to delete the personal information we hold about you or your child.
            This includes information you provided when enrolling, booking a trial, camp or workshop, contacting us
            through our website, or submitting an enquiry through one of our Facebook or Instagram lead forms.
          </p>
        </Section>

        <Section title="How to Request Deletion">
          <p>To request deletion, do any of the following:</p>
          <Bullets
            items={[
              'Email theteam@upaschool.com with the subject line "Data Deletion Request", telling us the name, email address and/or phone number the information was provided under.',
              'Call or message us on 07310 105678.',
              'Write to us or speak to a member of the team at any of our Milton Keynes venues.',
            ]}
          />
          <p>
            We may need to verify your identity before deleting anything, to make sure we don't remove someone's
            records at the wrong person's request.
          </p>
        </Section>

        <Section title="What Happens Next">
          <Bullets
            items={[
              'We will confirm receipt of your request and act on it within one month.',
              'We will delete your enquiry, contact and student records from our systems, including any details received from Facebook or Instagram lead forms.',
              'We will ask our service providers (such as our payment processor, Stripe) to delete associated personal data where applicable.',
              'Some records cannot be deleted immediately where the law requires us to keep them — for example, payment and financial records must be retained for 6 years under UK tax law. These are kept securely and deleted once the retention period ends.',
              'We will confirm to you in writing once the deletion is complete.',
            ]}
          />
        </Section>

        <Section title="Facebook and Instagram Enquiries">
          <p>
            If your details reached us through a Facebook or Instagram lead form, deleting your data from UPA's systems
            does not remove the copy Meta holds on its own platforms. You can manage or delete that information through
            your Facebook or Instagram account settings, under Meta's own privacy policy.
          </p>
        </Section>

        <Section title="Questions or Complaints">
          <p>
            If you have any questions, email{' '}
            <a href="mailto:theteam@upaschool.com" className="text-[#FF007F] hover:underline">theteam@upaschool.com</a>.
            If you are unhappy with how we handle your request, you can complain to the Information Commissioner's
            Office (ICO) at ico.org.uk.
          </p>
        </Section>

        <p className="text-white/40 text-sm mt-12">
          See also our{' '}
          <Link to="/privacy-policy" className="text-[#FF007F] hover:underline">Privacy Policy</Link>{' '}and{' '}
          <Link to="/terms-and-conditions" className="text-[#FF007F] hover:underline">Terms &amp; Conditions</Link>.
        </p>
      </main>
    </div>
  );
};

export default DataDeletion;
